import { useIntl } from "react-intl";
import { useState } from "react";
import { Modal, Text, FormFieldLayout } from "@wayflyer/flyui";

import { AccountsForm } from "./accounts-form";
import type { Account } from "./accounts-form";

export interface CompleteConnectionModalProps {
  header: string;
  body: string;
  open: boolean;
  info?: string;
  accounts?: Account[];
  connectClickHandler: (data: any) => Promise<void>;
  cancelClickHandler: () => void;
  confirmDisabled: (selectedAccounts) => boolean;
}

export const CompleteConnectionModal = ({
  header,
  body,
  info,
  accounts,
  open,
  connectClickHandler,
  cancelClickHandler,
  confirmDisabled,
}: CompleteConnectionModalProps) => {
  const intl = useIntl();
  const [selectedAccounts, setSelectedAccounts] = useState(accounts || []);

  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await connectClickHandler(selectedAccounts);
    } finally {
      setLoading(false);
    }
  };

  const renderAccountsListOrInfo = () => {
    // if info is present or there is only a single account with no properties then render info
    if (info || (accounts && accounts.length === 1 && !Array.isArray(accounts[0].properties))) {
      const text = info || ((accounts?.length === 1 && accounts[0].label) as string);
      return <Text>{text}</Text>;
    } else {
      return (
        <AccountsForm
          accounts={selectedAccounts}
          setAccounts={setSelectedAccounts}
          selectAllLabel={intl.formatMessage({
            id: "flyui: default select all accounts",
            defaultMessage: "Select all",
          })}
        />
      );
    }
  };

  return (
    <Modal
      title={header}
      primaryAction={{
        content: intl.formatMessage({ id: "shared-ui: Connect", defaultMessage: "Connect" }),
        onAction: handleConfirm,
        disabled: confirmDisabled(selectedAccounts),
        loading: loading,
      }}
      tertiaryAction={{
        content: intl.formatMessage({ id: "shared-ui: Cancel", defaultMessage: "Cancel" }),
        onAction: cancelClickHandler,
      }}
      isOpen={open}
      onClose={cancelClickHandler}
    >
      <FormFieldLayout>
        <Text>{body}</Text>
        {renderAccountsListOrInfo()}
      </FormFieldLayout>
    </Modal>
  );
};
